import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getMaxDepartureDate, MAX_STAY_NIGHTS, PIMService, SettingsService } from 'core-lib';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { EventSiteService } from '../../features/table-reservation/services/event-site.service';
import { IEventSite, ITimeBasedEvent } from '../../features/table-reservation/interfaces/interfaces';

export interface RoomData {
  adults: number;
  children: number;
  childAges: number[];
}

export interface DeeplinkParams {
  baseUrl: string;
  hotelCode: string;
  lang: string;
  arrivalMode: 'date' | 'today' | 'tomorrow' | 'days';
  arrivalDate: Date | null;
  arrivalDays: number | null;
  departureMode: 'date' | 'nights';
  departureDate: Date | null;
  nroOfNights: number | null;
  rooms: RoomData[];
  category: string;
  bookingCode: string;
  makeSearch: boolean;
  roomPriorityOrder: string;
  ratePriorityOrder: string;
}

export interface TableReservationDeeplinkParams {
  baseUrl: string;
  eventSiteId: string;
  guests: number | null;
  dateMode: 'date' | 'today' | 'tomorrow' | 'days';
  date: Date | null;
  days: number | null;
  eventId: string;
}

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-deeplink-generator',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    SelectModule,
    InputTextModule,
    DatePickerModule,
    InputNumberModule,
    ButtonModule,
    RadioButtonModule,
    CheckboxModule,
    TextareaModule,
    ToggleSwitchModule
  ],
  templateUrl: './deeplink-generator.component.html',
  styleUrl: './deeplink-generator.component.scss'
})
export class DeeplinkGeneratorComponent implements OnInit, OnDestroy {
  mode: 'room' | 'table' = 'room';

  hotels: SelectOption[] = [];
  languageOptions: SelectOption[] = [];
  categoryOptions: SelectOption[] = [];
  rooms: RoomData[] = [{ adults: 2, children: 0, childAges: [] }];
  generatedUrl = '';

  restaurants: SelectOption[] = [];
  eventOptions: SelectOption[] = [];
  generatedTableUrl = '';
  readonly maxStayNights = MAX_STAY_NIGHTS;
  // latest departure date the availability API accepts for the selected arrival date
  departureMaxDate: Date | null = null;
  private allTimeBasedEvents: ITimeBasedEvent[] = [];

  private subs: Subscription[] = [];

  form = new FormGroup({
    hotelCode: new FormControl<string>(''),
    lang: new FormControl<string>(''),
    arrivalMode: new FormControl<'date' | 'today' | 'tomorrow' | 'days'>('date'),
    arrivalDate: new FormControl<Date | null>(null),
    arrivalDays: new FormControl<number | null>(null),
    departureMode: new FormControl<'date' | 'nights'>('nights'),
    departureDate: new FormControl<Date | null>(null),
    nroOfNights: new FormControl<number | null>(1),
    category: new FormControl<string>(''),
    bookingCode: new FormControl<string>(''),
    makeSearch: new FormControl<boolean>(true),
    roomPriorityOrder: new FormControl<string>(''),
    ratePriorityOrder: new FormControl<string>(''),
  });

  trForm = new FormGroup({
    eventSiteId: new FormControl<string>(''),
    guests: new FormControl<number | null>(2),
    dateMode: new FormControl<'date' | 'today' | 'tomorrow' | 'days'>('date'),
    date: new FormControl<Date | null>(null),
    days: new FormControl<number | null>(null),
    eventId: new FormControl<string>('')
  });

  constructor(
    private settings: SettingsService,
    private pim: PIMService,
    private translate: TranslateService,
    private eventSiteService: EventSiteService
  ) {}

  ngOnInit() {
    this.loadHotels();
    this.loadRestaurants();
    this.loadTimeBasedEvents();
    this.subs.push(this.form.valueChanges.subscribe(() => this.updateUrl()));
    this.subs.push(this.form.controls.arrivalDate.valueChanges.subscribe(arrival => this.updateDepartureMaxDate(arrival)));
    this.subs.push(this.trForm.valueChanges.subscribe(() => {
      this.updateTableUrl();
      this.updateEventOptions();
    }));
    this.updateUrl();
    this.updateTableUrl();
  }

  private loadRestaurants() {
    this.eventSiteService.getEventSites().subscribe({
      next: (sites: IEventSite[]) => {
        this.restaurants = sites.map(s => ({ label: s.name, value: s.id }));
        if (this.restaurants.length === 1) {
          this.trForm.patchValue({ eventSiteId: this.restaurants[0].value });
        }
      },
      error: () => { this.restaurants = []; }
    });
  }

  private loadTimeBasedEvents() {
    this.eventSiteService.searchTimeBasedEvents().subscribe({
      next: (events) => {
        this.allTimeBasedEvents = events;
        this.updateEventOptions();
      },
      error: () => {
        this.allTimeBasedEvents = [];
        this.updateEventOptions();
      }
    });
  }

  private updateEventOptions() {
    const eventSiteId = this.trForm.value.eventSiteId;
    const events = this.allTimeBasedEvents.filter(e => e.eventSiteId === eventSiteId);

    this.eventOptions = [
      { label: this.translate.instant('dg.trSelectEvent'), value: '' },
      ...events.map(e => ({
        label: e.status === 'ACTIVE' ? e.name : `${e.name} (${this.translate.instant('dg.trInactive')})`,
        value: e.id
      }))
    ];

    if (this.trForm.value.eventId && !events.some(e => e.id === this.trForm.value.eventId)) {
      this.trForm.patchValue({ eventId: '' });
    }
  }

  private loadHotels() {
    const endpoints: { hotelCode: string }[] = this.settings.get('app.endpoints') ?? [];
    this.hotels = endpoints.map(ep => {
      const info = this.pim.getHotelInfo(ep.hotelCode);
      return { label: info?.name ?? ep.hotelCode, value: ep.hotelCode };
    });
    if (this.hotels.length === 1) {
      this.form.patchValue({ hotelCode: this.hotels[0].value });
    }

    const langs: string[] = this.settings.get('app.languages') ?? [];
    this.languageOptions = langs.map(l => ({
      label: this.translate.instant(`dg.langNames.${l}`) || l,
      value: l
    }));

    const cats: { value: string; name?: { fi?: string; en?: string } }[] = this.settings.get('searchMask.categories') ?? [];
    this.categoryOptions = [
      { label: this.translate.instant('dg.noRestriction'), value: '' },
      ...cats.map(c => ({ label: c.name?.fi ?? c.name?.en ?? c.value, value: c.value }))
    ];
  }

  addRoom() {
    if (this.rooms.length < 4) {
      this.rooms = [...this.rooms, { adults: 1, children: 0, childAges: [] }];
      this.updateUrl();
    }
  }

  removeRoom(index: number) {
    if (this.rooms.length > 1) {
      this.rooms = this.rooms.filter((_, i) => i !== index);
      this.updateUrl();
    }
  }

  updateRoomAdults(index: number, value: number) {
    this.rooms = this.rooms.map((r, i) => i === index ? { ...r, adults: value } : r);
    this.updateUrl();
  }

  updateRoomChildren(index: number, value: number) {
    const room = this.rooms[index];
    const newAges = Array.from({ length: value }, (_, ai) => room.childAges[ai] ?? 0);
    this.rooms = this.rooms.map((r, i) => i === index ? { ...r, children: value, childAges: newAges } : r);
    this.updateUrl();
  }

  updateChildAge(roomIndex: number, ageIndex: number, value: number) {
    const room = { ...this.rooms[roomIndex], childAges: [...this.rooms[roomIndex].childAges] };
    room.childAges[ageIndex] = value;
    this.rooms = this.rooms.map((r, i) => i === roomIndex ? room : r);
    this.updateUrl();
  }

  updateDepartureMaxDate(arrival: Date | null) {
    this.departureMaxDate = arrival ? getMaxDepartureDate(arrival) : null;
  }

  copyUrl(url: string) {
    navigator.clipboard.writeText(url).catch(err => {
      console.error('Clipboard write failed', err);
    });
  }

  private updateUrl() {
    const f = this.form.value;
    this.generatedUrl = this.buildUrl({
      baseUrl: this.settings.get('app.bookingEngineUrl') ?? '',
      hotelCode: f.hotelCode ?? '',
      lang: f.lang ?? '',
      arrivalMode: f.arrivalMode ?? 'date',
      arrivalDate: f.arrivalDate ?? null,
      arrivalDays: f.arrivalDays ?? null,
      departureMode: f.departureMode ?? 'nights',
      departureDate: f.departureDate ?? null,
      nroOfNights: f.nroOfNights ?? null,
      rooms: this.rooms,
      category: f.category ?? '',
      bookingCode: f.bookingCode ?? '',
      makeSearch: f.makeSearch ?? false,
      roomPriorityOrder: f.roomPriorityOrder ?? '',
      ratePriorityOrder: f.ratePriorityOrder ?? ''
    });
  }

  buildUrl(params: DeeplinkParams): string {
    const base = params.baseUrl.replace(/\/$/, '') + '/deeplink';
    const qp = new URLSearchParams();

    if (params.lang) qp.set('lang', params.lang);
    if (params.hotelCode) qp.set('hotelCode', params.hotelCode);

    if (params.arrivalMode === 'today') {
      qp.set('arrival', 'today');
    } else if (params.arrivalMode === 'tomorrow') {
      qp.set('arrival', 'tomorrow');
    } else if (params.arrivalMode === 'days' && params.arrivalDays != null) {
      qp.set('arrival', `days${params.arrivalDays}`);
    } else if (params.arrivalMode === 'date' && params.arrivalDate) {
      qp.set('arrival', dayjs(params.arrivalDate).format('DD-MM-YYYY'));
    } else if (params.arrivalMode === 'date') {
      qp.set('arrival', 'today');
    }

    if (params.departureMode === 'date' && params.departureDate) {
      qp.set('departure', dayjs(params.departureDate).format('DD-MM-YYYY'));
    } else if (params.departureMode === 'nights' && params.nroOfNights != null) {
      qp.set('nroOfNights', String(params.nroOfNights));
    }

    params.rooms.forEach((room, i) => {
      const ages = room.childAges.slice(0, room.children).join('-');
      qp.set(`room${i}`, `${room.adults}|${room.children}|${ages}`);
    });

    if (params.category) qp.set('category', params.category);
    if (params.bookingCode) qp.set('bookingCode', params.bookingCode);
    if (params.makeSearch) qp.set('makeSearch', '1');
    if (params.roomPriorityOrder) qp.set('roomPriorityOrder', params.roomPriorityOrder);
    if (params.ratePriorityOrder) qp.set('ratePriorityOrder', params.ratePriorityOrder);

    const qs = qp.toString();
    return qs ? `${base}?${qs}` : base;
  }

  private updateTableUrl() {
    const f = this.trForm.value;
    this.generatedTableUrl = this.buildTableReservationUrl({
      baseUrl: this.settings.get('app.bookingEngineUrl') ?? '',
      eventSiteId: f.eventSiteId ?? '',
      guests: f.guests ?? null,
      dateMode: f.dateMode ?? 'date',
      date: f.date ?? null,
      days: f.days ?? null,
      eventId: f.eventId ?? ''
    });
  }

  buildTableReservationUrl(params: TableReservationDeeplinkParams): string {
    const base = params.baseUrl.replace(/\/$/, '') + '/restaurant/deeplink';
    const qp = new URLSearchParams();

    if (params.eventSiteId) qp.set('eventSiteId', params.eventSiteId);
    if (params.guests != null) qp.set('guests', String(params.guests));

    if (params.dateMode === 'today') {
      qp.set('date', 'today');
    } else if (params.dateMode === 'tomorrow') {
      qp.set('date', 'tomorrow');
    } else if (params.dateMode === 'days' && params.days != null) {
      qp.set('date', `days${params.days}`);
    } else if (params.dateMode === 'date' && params.date) {
      qp.set('date', dayjs(params.date).format('DD-MM-YYYY'));
    }

    if (params.eventId) qp.set('eventId', params.eventId);

    const qs = qp.toString();
    return qs ? `${base}?${qs}` : base;
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }
}
